import { useRef } from 'react';

import clsx from 'clsx';

import {
  FeaturedEventPromotionEvent,
  createFeaturedEvent,
} from '@/common/analytics/gtm/Homepage/featuredEventsAnalytics';
import { trackElementInteraction } from '@/common/analytics/gtm/shared/trackElementInteraction';
import { trackGA4SelectPromotion } from '@/common/analytics/gtm/shared/trackPromotionClick';
import { trackG4ViewPromotion } from '@/common/analytics/gtm/shared/trackViewPromotion';
import Avatar from '@/common/components/Avatar/Avatar';
import Badge from '@/common/components/Badge/Badge';
import CounterToBadge from '@/common/components/CounterToBadge/CounterToBadge';
import Video from '@/common/components/Video/Video';
import { ResolutionsSrcSet, StaticImage } from '@/common/constants/displateRoutes';
import { useOnScreen } from '@/common/hooks/useOnScreen/useOnScreen';
import { Button } from '@/common/styleguide/atoms/Button/Button';
import { HomepageFeaturedEventV2 } from 'generatedBfw/static';

import styles from './featuredEvent.module.scss';
import FeaturedEventHead from './FeaturedEventHead';

interface Props {
  event: HomepageFeaturedEventV2;
}

const FeaturedEventView = ({ event }: Props) => {
  const sectionRef = useRef(null);
  useOnScreen(sectionRef, {
    once: true,
    callback: () => trackG4ViewPromotion<FeaturedEventPromotionEvent>(createFeaturedEvent(event)),
  });

  const handleFeaturedEventClick = () => {
    trackGA4SelectPromotion<FeaturedEventPromotionEvent>(createFeaturedEvent(event));
    trackElementInteraction({ elementId: 'featured_event_see_all_link' });
  };

  const renderTopElement = () => {
    if (event.files?.badgeRetina) {
      return (
        <img
          alt=""
          className={styles.promoBadge}
          data-testid="featured-event-promo-badge"
          height={192}
          loading="lazy"
          src={event.files.badgeRetina}
          width={192}
        />
      );
    }
    if (event.author) {
      return (
        <Avatar
          author={event.author}
          className={styles.avatar}
          onClick={() => trackElementInteraction({ elementId: 'featured_event_logo' })}
        />
      );
    }
    return <div />;
  };

  return (
    <section className={styles.container} data-testid="featured-event" ref={sectionRef}>
      {event.files && <FeaturedEventHead files={event.files} video={event.video} />}
      {renderTopElement()}
      <div className={styles.detailsContainer}>
        <div className={styles.badges}>
          {event.badge ? <Badge type={event.badge} /> : null}
          {event.endsAt ? <CounterToBadge endsAt={event.endsAt} /> : null}
        </div>
        <a
          className={styles.link}
          data-testid="featured-event-title"
          href={event.url}
          onClick={handleFeaturedEventClick}
        >
          <h3 className={styles.heading}>{event.title}</h3>
        </a>
        {/* we are basing button variant on props and want to avoid layout shift on the two buttons below */}
        {event.callToAction ? (
          <>
            <Button
              className={clsx(styles.button, styles.buttonMobile)}
              data-testid="featured-event-button"
              size="large"
            >
              {event.callToAction}
            </Button>
            <Button className={styles.button} data-testid="featured-event-button" size="default">
              {event.callToAction}
            </Button>
          </>
        ) : null}
        {event.files && !event.video?.source ? (
          <picture>
            <source
              media="(min-width: 483px)"
              srcSet={ResolutionsSrcSet({
                x1: event.files.desktopStandard,
                x2: event.files.desktopRetina,
              })}
            />
            <source
              srcSet={ResolutionsSrcSet({
                x1: event.files.mobileStandard,
                x2: event.files.mobileRetina,
              })}
            />
            <img
              alt={event.title}
              className={styles.image}
              data-testid="featured-event-image"
              // @ts-expect-error ignore rule for react to pass tests - fetchpriority is in experimental state
              fetchpriority="high"
              src={event.files.mobileRetina} // preload higher density image as it seem to be default for most mobile devices, thus saving us one request
            />
          </picture>
        ) : null}
        {event.video?.source ? (
          <>
            <Video
              autoPlay
              className={styles.video}
              data-testid="featured-event-video"
              loop
              muted
              playsInline
              poster={
                event.files?.desktopStandard ||
                StaticImage('/images/homepage/featuredEvent/default_poster.jpg')
              }
              preload="auto"
              src={event.video.source}
            />
            <div className={styles.gradient} />
          </>
        ) : null}
      </div>
    </section>
  );
};

export default FeaturedEventView;
